<template>
  <div
    v-show="show && isVisible"
    :id="`field-${fieldPath}`"
    data-test="dynamic-field"
    :class="['field-wrapper', 'position-relative', { 'field-hidden': !isVisible }]"
  >
    <AppAlert
      v-if="$v && $v.$errors.length && field.useErrorAlert && !field.__hideValidationError"
      variant="danger"
      class="d-flex align-items-center"
    >
      <AppIcon
        name="error"
        class="pr-2"
      />
      <div>
        <p class="mb-0">
          <span>{{ field.errorMessage || 'Missing information:' }}</span>
          <strong
            v-for="(error, index) in $v.$errors"
            :key="index"
          >
            {{ error.$message }}
          </strong>
        </p>
      </div>
    </AppAlert>
    <template v-if="isDiffingActive">
      <DynamicFieldDiff
        v-if="displayDiffInReadMode || editable"
        :baseValue="baseValue"
        :currentValue="fieldValue"
        :diffs="proposalFieldsDiffs"
        :isEditable="isEditable"
        :elevateIconToCardLevel="isOnlyFieldInContainer && hasNestedFields"
        @resolve="update"
      >
        <template #base="{ value, formValue }">
          <component
            :is="fieldComponent"
            :question="field"
            v-bind="{ ...field, ...propsExtension }"
            :value="value"
            :editable="false"
            :disabled="true"
            :isolatedMode="true"
            :formValue="formValue"
            :dataTest="`diff_${field.questionId}`"
          />
        </template>
        <template #current>
          <component
            :is="fieldComponent"
            :question="field"
            v-bind="{ ...field, ...propsExtension }"
            :value="fieldValue"
            :editable="false"
            :disabled="true"
            :isolatedMode="true"
            :formValue="formValue"
            :dataTest="`diff_${field.questionId}`"
          />
        </template>
        <template #diff="{ value, formValue }">
          <component
            :is="fieldComponent"
            :question="field"
            v-bind="{ ...field, ...propsExtension }"
            :value="value"
            :editable="false"
            :disabled="true"
            :isolatedMode="true"
            :formValue="formValue"
            :dataTest="`diff_${field.questionId}`"
          />
        </template>
      </DynamicFieldDiff>
    </template>
    <component
      :is="fieldComponent"
      v-bind="{ ...field, ...propsExtension }"
      :question="field"
      :iteration="iteration"
      :value="fieldValue"
      :formValue="formValue"
      :formId="formId"
      :formType="formType"
      :error="$v.$errors.map(e => e.$message).join(', ')"
      :invalid="$v.$invalid"
      :dirty="$v.$anyDirty"
      :disabled="!isEditable"
      :editable="isEditable"
      :dataScope="dataScope"
      :dataTest="field.questionId"
      :allowFilePrivacyToggle="allowFilePrivacyToggle"
      @input="update"
      @blur="$v.$touch"
    />
    <DynamicFieldErrors
      v-if="errorsToDisplay && errorsToDisplay.length && !field.useErrorAlert && !field.__hideValidationError"
      :errors="errorsToDisplay"
    />
  </div>
</template>

<script>
import { computed, ref, inject, markRaw, provide } from '@vue/composition-api'
import DynamicFieldErrors from './DynamicFieldErrors'
import useFieldActions from './useFieldActions'
import useField from './useField'
import useFieldValidations from './useFieldValidations'
import useDataSources from './useDataSources'
import useFieldStatus from './useFieldStatus'
import DynamicFieldDiff from './DynamicFieldDiff'
import useFieldDiffs from './useFieldDiffs'
import useFieldDefaults from './useFieldDefaults'
import { userHasAnyAllowedRole } from '@shared/permissions/validators'
import store from '@/store'
import WithDescriptionLong from '@/components/Forms/WithDescriptionLong'
import bugsnagClient from '@/scripts/bugsnag.js'
import { DataError } from '@/utils/errors'

function withDescription (Comp, field) {
  const CompWithDesc = ({ props, listeners, attrs, data }) =>
    <WithDescriptionLong descriptionLong={field.descriptionLong}>
      <Comp {...{ ...props, ...listeners, ...attrs, ...data }} />
    </WithDescriptionLong>

  return CompWithDesc
}

export default {
  components: { DynamicFieldErrors, DynamicFieldDiff },
  props: {
    questionId: {
      type: String,
      required: true,
    },
    formId: {
      type: String,
      required: true,
    },
    formType: {
      type: String,
      required: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    show: {
      type: Boolean,
      default: true,
    },
    // It's a part of DynamicForm's template scoping,
    // but it allows to limit visibility of certain data (e.g. element in array).
    // Logic has to be implemented in field component
    dataScope: {
      type: Array,
      default: () => [],
    },
    allowFilePrivacyToggle: {
      type: Boolean,
      default: false,
    },
    isOnlyFieldInContainer: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const iteration = ref(0)
    const user = store.state.user
    const formValue = inject(`${props.formId}_value`)
    const questions = inject(`${props.formId}_questions`)
    const editableChecks = inject('editableChecksReplacement', [])
    const isInRequest = inject('isInRequest', false)
    const actionsDisabled = inject('dynamic_form_actions_disabled', ref(false))
    const isReadonly = inject('dynamic_form_readonly', ref(false))

    const dynamicFieldQuestionPath = inject('dynamic_field_question_path', [])
    const dynamicFieldValuePath = inject('dynamic_field_value_path', dynamicFieldQuestionPath)
    provide('dynamic_field_question_path', [ ...dynamicFieldQuestionPath, props.questionId ])
    provide('dynamic_field_value_path', [ ...dynamicFieldValuePath, props.questionId ])

    const { sourceData: contextSources } = useDataSources()

    function extractField (questionId) {
      let fieldDefinition = questions.value
        .find(question => {
          if (!question.hasOwnProperty('questionId')) {
            const err = new DataError(`Question doesn't have 'questionId' key: ${question}`)
            bugsnagClient.notify(err)
          }
          return question.questionId === questionId
        })

      if (!fieldDefinition) {
        const err = new DataError(`Question with id ${questionId} doesn't exist in provided questions.`)
        bugsnagClient.notify(err)
        // Empty field definition to avoid 'Cannot read property of undefined' errors spam in console & bugsnag
        fieldDefinition = {
          config: {},
          rolesAllowedToEdit: [],
          termsAllowedToEdit: [],
          rolesAllowedToEditRequest: [],
        }
      }

      const config = props.config.predefinedContent
        ? { ...(props.config.predefinedContent.config || {}), ...fieldDefinition.config }
        : fieldDefinition.config

      const rolesAllowedToEdit = Array.isArray(props.config?.rolesAllowedToEdit) && !fieldDefinition.rolesAllowedToEdit
        ? props.config.rolesAllowedToEdit
        : fieldDefinition.rolesAllowedToEdit

      const termsAllowedToEdit = Array.isArray(props.config?.termsAllowedToEdit) && !fieldDefinition.termsAllowedToEdit
        ? props.config.termsAllowedToEdit
        : fieldDefinition.termsAllowedToEdit

      const rolesAllowedToEditRequest = Array.isArray(props.config?.rolesAllowedToEditRequest) && !fieldDefinition.rolesAllowedToEditRequest
        ? props.config.rolesAllowedToEditRequest
        : fieldDefinition.rolesAllowedToEditRequest

      return {
        ...fieldDefinition,
        rolesAllowedToEdit,
        termsAllowedToEdit,
        rolesAllowedToEditRequest,
        config,
      }
    }

    const f = extractField(props.questionId)

    const field = f.__forceComputed
      // NOTE: We only want to use computed for very limited set of fields
      //       as extensively relying on this will slow down the form significantly.
      ? computed(() => extractField(props.questionId))
      // NOTE: Fake a ref. Can remove that once we refactor composables to expect plain object
      : { value: f }

    const propsExtension = inject(field.value.propsExtensionKey, {})

    const {
      fieldComponent,
      fieldValue,
      fieldPath,
      update,
    } = useField({ id: props.questionId, formValue, field: field.value, formId: props.formId })
    const onActionUpdate = (...args) => {
      iteration.value++
      update(...args)
    }

    const { fieldWithChanges } = useFieldActions({
      field,
      formValue,
      fieldValue,
      update: onActionUpdate,
      contextSources,
      isReadonly,
      actionsDisabled,
      dynamicFieldValuePath,
    })

    useFieldDefaults({ field: fieldWithChanges, fieldValue, update, isReadonly })
    useFieldStatus({
      field,
      fieldValue,
      fieldPath,
      formId: props.formId,
    })

    const { $v } = useFieldValidations({
      field: fieldWithChanges,
      fieldValue,
      // Use path as id, so we can track nested fields
      id: [ ...dynamicFieldValuePath, props.questionId ].join('.'),
    })

    const userHasEditAccess = computed(() => userHasAnyAllowedRole(user, null, fieldWithChanges.value.rolesAllowedToEdit))

    const isEditable = computed(() =>
      [
        ...(isInRequest
          ? editableChecks.map(check => check(fieldWithChanges.value))
          : (editableChecks.length
            ? editableChecks.map(check => check(fieldWithChanges.value))
            : [ userHasEditAccess.value ]
          )),
        props.editable,
        !fieldWithChanges.value.disabled,
      ].every(Boolean)
    )

    const isVisible = computed(
      () => userHasAnyAllowedRole(user, null, fieldWithChanges.value.rolesAllowedToSee) &&
        !fieldWithChanges.value.hidden
    )

    const fieldComponentType = field.value.descriptionLong
      ? markRaw(withDescription(fieldComponent.type, field.value))
      : markRaw(fieldComponent.type)

    const {
      proposalFieldsDiffs,
      baseValue,
      isDiffingActive,
      displayDiffInReadMode,
    } = useFieldDiffs({
      field: fieldWithChanges,
      questionPath: dynamicFieldQuestionPath,
      valuePath: dynamicFieldValuePath,
    })

    const isQuestionWithNestedFieldsType = field.value.questionType === 'questionWithNestedFields'

    // This is questionWithNestedFields-only settings
    // Most of the time it's enough to display error message below nested field, no need to repeat it below
    // container. It creates weird priamids of errors
    const hideCollectedValidationMessages = isQuestionWithNestedFieldsType
      ? !!field.value.config?.hideCollectedValidationMessages
      : false

    // If hideCollectedValidationMessages is true, limit errors to only this field related (omit nested errors)
    const errorsToDisplay = computed(() => {
      if (!hideCollectedValidationMessages) return $v.value?.$errors
      return $v.value?.$errors?.filter(e => e.$propertyPath === fieldPath)
    })

    return {
      fieldComponent: fieldComponentType,
      field: fieldWithChanges,
      fieldValue,
      formValue,
      update,
      fieldPath,
      $v,
      isEditable,
      isVisible,
      iteration,
      isReadonly,
      actionsDisabled,
      proposalFieldsDiffs,
      baseValue,
      isDiffingActive,
      displayDiffInReadMode,
      dynamicFieldQuestionPath,
      dynamicFieldValuePath,
      propsExtension,
      hideCollectedValidationMessages,
      errorsToDisplay,
    }
  },
  computed: {
    hasNestedFields () {
      return !!this.field.config?.fields
    },
  },
}
</script>

<style scoped>
.field-wrapper {
  margin-bottom: 0.5rem;
}

.field-hidden {
  flex: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  visibility: hidden;
}
</style>
